/**
 * Decision 147 (Mark's bug: Links & resend → "Copy link" said Copied but
 * wasn't): copying text that ISN'T KNOWN YET at click time.
 *
 * Browsers only grant clipboard writes during a user gesture. Any `await`
 * before `navigator.clipboard.writeText()` — the mint API call here — spends
 * the gesture; Safari then rejects the write outright (Chromium is more
 * lenient but not guaranteed). The old code swallowed that rejection and
 * showed "✓ Copied" regardless.
 *
 * The fix is the async-clipboard spec's own mechanism: hand the browser a
 * ClipboardItem whose content is a PROMISE — the write is authorised
 * synchronously inside the gesture, and the text arrives when the fetch
 * resolves. Where ClipboardItem isn't available, fall back to writeText
 * after the fetch (works in Chromium, where clipboard-write is auto-granted
 * to the focused tab).
 *
 * Returns whether the text actually reached the clipboard — callers must
 * only claim "Copied" on true, and offer a manual copy of the (now known)
 * text otherwise: a second click is a fresh gesture, so a plain writeText
 * from state always works.
 *
 * ⚠️ Call this BEFORE any await in the click handler, passing the fetch
 * promise — calling it after an await recreates the original bug.
 */
export function copyTextFromPromise(text: Promise<string>): Promise<boolean> {
  const fallback = async (): Promise<boolean> => {
    try {
      await navigator.clipboard.writeText(await text)
      return true
    } catch {
      return false
    }
  }
  try {
    if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
      return navigator.clipboard.write([
        new ClipboardItem({ 'text/plain': text.then((t) => new Blob([t], { type: 'text/plain' })) }),
      ]).then(() => true, fallback)
    }
  } catch { /* ClipboardItem rejected the promise shape — fall through */ }
  return fallback()
}
